import { BaseEventProps, send } from './_send';

// -----------------------------------------------------------------------------
// Creator/admin dashboard actions. Content CRUD, audience tooling, e-commerce,
// AI assistant, and data exports. Monetization tier CRUD lives in
// `monetization-events.ts`; poll/leaderboard CRUD live in their own modules.
// -----------------------------------------------------------------------------

// --- Badges / NFT issuance ---------------------------------------------------

export const trackBadgeCreated = (
  data: BaseEventProps & {
    badge_id?: string;
    badge_type?: string;
    is_draft?: boolean;
    has_collected_perks?: boolean;
  },
) => send('badge_created', data);

export const trackBadgeUpdated = (
  data: BaseEventProps & {
    badge_id?: string;
  },
) => send('badge_updated', data);

export const trackBadgeDeleted = (
  data: BaseEventProps & {
    badge_id?: string;
  },
) => send('badge_deleted', data);

export const trackClaimCodesGenerated = (
  data: BaseEventProps & {
    badge_id?: string;
    count?: number;
    export_format?: 'csv' | 'pdf';
  },
) => send('claim_codes_generated', data);

// --- Challenges --------------------------------------------------------------

export const trackChallengeCreated = (
  data: BaseEventProps & {
    challenge_id?: string;
    challenge_type?: string;
  },
) => send('challenge_created', data);

export const trackChallengeUpdated = (
  data: BaseEventProps & {
    challenge_id?: string;
  },
) => send('challenge_updated', data);

export const trackChallengeDeleted = (
  data: BaseEventProps & {
    challenge_id?: string;
  },
) => send('challenge_deleted', data);

// --- CMS / notebooks ---------------------------------------------------------

export const trackNotebookCreated = (
  data: BaseEventProps & {
    notebook_id?: string;
  },
) => send('notebook_created', data);

export const trackNotebookUpdated = (
  data: BaseEventProps & {
    notebook_id?: string;
  },
) => send('notebook_updated', data);

export const trackNotebookDeleted = (
  data: BaseEventProps & {
    notebook_id?: string;
  },
) => send('notebook_deleted', data);

// --- Cohorts / fan segments --------------------------------------------------

export const trackCohortCreated = (
  data: BaseEventProps & {
    cohort_id?: string;
    filter_count?: number;
  },
) => send('cohort_created', data);

export const trackCohortUpdated = (
  data: BaseEventProps & {
    cohort_id?: string;
  },
) => send('cohort_updated', data);

export const trackCohortDeleted = (
  data: BaseEventProps & {
    cohort_id?: string;
  },
) => send('cohort_deleted', data);

export const trackCohortReportGenerated = (
  data: BaseEventProps & {
    cohort_id?: string;
  },
) => send('cohort_report_generated', data);

// --- Fan management & exports ------------------------------------------------

export const trackFanSearched = (
  data: BaseEventProps & {
    query_length?: number;
    result_count?: number;
  } = {},
) => send('fan_searched', data);

export const trackFanProfileViewed = (
  data: BaseEventProps & {
    fan_id?: string;
  } = {},
) => send('fan_profile_viewed', data);

export const trackCsvReportExported = (
  data: BaseEventProps & {
    report_type: string;
    row_count?: number;
  },
) => send('csv_report_exported', data);

export const trackDashboardAnalyticsViewed = (
  data: BaseEventProps & {
    date_range?: string;
  } = {},
) => send('dashboard_analytics_viewed', data);

// --- Badge redemption portal -------------------------------------------------

export const trackBadgeRedeemed = (
  data: BaseEventProps & {
    badge_id?: string;
    fan_id?: string;
  },
) => send('badge_redeemed', data);

// --- E-commerce (Shopify) ----------------------------------------------------

export const trackShopifyConnected = (
  data: BaseEventProps & {
    store_url?: string;
  } = {},
) => send('shopify_connected', data);

export const trackProductMappingCreated = (
  data: BaseEventProps & {
    badge_id?: string;
    product_id?: string;
  },
) => send('product_mapping_created', data);

export const trackProductMappingUpdated = (
  data: BaseEventProps & {
    mapping_id?: string;
    status?: string;
  },
) => send('product_mapping_updated', data);

export const trackProductSyncTriggered = (data: BaseEventProps = {}) =>
  send('product_sync_triggered', data);

// --- Team & collaboration ----------------------------------------------------

export const trackTeamMemberInvited = (
  data: BaseEventProps & {
    role?: string;
  } = {},
) => send('team_member_invited', data);

export const trackTeamMemberRemoved = (data: BaseEventProps = {}) =>
  send('team_member_removed', data);

// --- AI data assistant -------------------------------------------------------

export const trackAiAssistantQuerySent = (
  data: BaseEventProps & {
    query_length?: number;
  } = {},
) => send('ai_assistant_query_sent', data);
